import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { TreeNodeOrNodeSlug } from 'react-common'
import { useParams } from 'react-router-dom'
import { ROUTE_BASE } from 'apps-common'
import { v4 as uuid } from 'uuid'
import { ActivityType } from '@/api/models/activity'
import {
  ActivityWorkflowState,
  GradeRevealPolicy,
  ObjectiveWorkflowAggregate,
  ObjectiveWorkflowState,
} from '@/api/models/aggregate'
import { useAuth } from '@/features/auth/useAuth'
import { useSidebarLayers } from '@/features/layout/hooks/useSidebarLayers'
import { useBreadcrumbs } from '@/hooks/useBreadcrumbs'
import { useCourseNavigate } from '@/hooks/useCourseNavigate'
import { useEventLogger } from '@/hooks/useEventLogger'
import { useGetCourseDetailsSteps } from '@/hooks/useGetCourseDetailsSteps'
import { useIsCatalogCourse } from '@/hooks/useIsCatalogCourse'
import { useSetBackUrl } from '@/hooks/useSetBackUrl'
import { useGetObjectiveAccessByIdQuery } from '@/store/api/aggregatesApi'
import { useLazyFetchActivityWorkflowAggregateFullTreeQuery } from '@/store/api/objectiveWorkflowApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { fetchMyActivityRecordsAsync } from '@/store/slices/activityRecordsSlice'
import {
  fetchMyAggregatesAsync,
  fetchObjectiveRecordsAsync,
  startActivityWorkflowAsync,
  startObjectiveWorkflowAsync,
} from '@/store/slices/aggregateSlice'
import { addBlockingOperation, removeBlockingOperation } from '@/store/slices/loadingSlice'
import styles from './CourseDetails.module.scss'
import { CourseDetailsComponent } from './CourseDetailsComponent'
import { useShouldRedirectToCatalogOrEnrolledCourse } from './hooks/useShouldRedirectToCatalogOrEnrolledCourse'
import { useIsMigratedCourse } from '../CourseInfo/components/EnrolledCourseButtons/useIsMigratedCourse'

type PageParams = 'objectiveId' | 'aggregateId'

interface CourseDetailsProps {
  onError: VoidFunction
}

export const CourseDetails: FC<CourseDetailsProps> = ({ onError }) => {
  const { objectiveId = '' } = useParams<PageParams>()

  const isCatalogCourse = useIsCatalogCourse()

  const dispatch = useAppDispatch()
  const { logEvent } = useEventLogger()

  const objectiveRecords = useAppSelector((store) => store.aggregate.records)
  const activityRecords = useAppSelector((store) => store.activityRecords.activityRecords)

  const [eventDispatched, setEventDispatched] = useState(false)

  const { navigateToContent } = useCourseNavigate()
  const { setMyCourseBreadcrumbs } = useBreadcrumbs()
  const { setLayers } = useSidebarLayers()

  const [fetchActivityWorkflowAggregateFullTreeQuery] = useLazyFetchActivityWorkflowAggregateFullTreeQuery()

  const { data: aggregate, refetch: refetchGetObjectiveAccessById } = useGetObjectiveAccessByIdQuery(objectiveId, {
    skip: !objectiveId,
  })

  const objective = useMemo(() => aggregate?.objective, [aggregate])

  useSetBackUrl('/')
  useShouldRedirectToCatalogOrEnrolledCourse(aggregate)

  const getSupportReview = useCallback(() => {
    if (!objective?.activity?.supportReview) {
      return false
    }
    if (aggregate?.objectiveWorkflowAggregate?.lastObjectiveWorkflow?.state !== ObjectiveWorkflowState.finished) {
      return false
    }

    switch (aggregate.gradeRevealPolicy) {
      case GradeRevealPolicy.Never:
        return false
      case GradeRevealPolicy.AfterDate: {
        const revealDate = new Date(aggregate.gradeRevealDate || '').getTime()
        return revealDate < Date.now()
      }
      case GradeRevealPolicy.None:
      case GradeRevealPolicy.AfterFinish:
      default:
        return true
    }
  }, [aggregate, objective?.activity?.supportReview])

  const isMigratedCourse = useIsMigratedCourse(objectiveRecords)

  const { sidebarLayers: sidebarItems } = useGetCourseDetailsSteps({
    isLti: objective?.activity?.type === ActivityType.lti,
    inProgress:
      aggregate?.objectiveWorkflowAggregate?.lastObjectiveWorkflow?.state === ObjectiveWorkflowState.inProgress,
    objectiveId,
    supportReview: getSupportReview() && !isMigratedCourse, // for the migrated courses, the review is not available because there is no objectiveWorkflow
  })

  const { user } = useAuth()

  const userId = user?.id
  const objectiveActivityId = objective?.activity?.id
  const objectiveWorkflowId = aggregate?.objectiveWorkflowAggregate?.lastObjectiveWorkflow?.id

  useEffect(() => {
    if (isCatalogCourse || !objectiveId || !aggregate?.objectiveWorkflowAggregate) return

    dispatch(addBlockingOperation())

    Promise.all([
      refetchGetObjectiveAccessById().unwrap(),
      dispatch(fetchObjectiveRecordsAsync(aggregate.objectiveWorkflowAggregate.id)).unwrap(),
    ])
      .catch(onError)
      .finally(() => {
        dispatch(removeBlockingOperation())
      })
  }, [aggregate, objectiveId, isCatalogCourse, refetchGetObjectiveAccessById, onError, dispatch])

  const getActivityWorkflowAggregates = useCallback(
    async (objectiveWorkflowIdParam?: string) => {
      const workflowId = objectiveWorkflowIdParam || objectiveWorkflowId

      if (!objectiveActivityId || !userId || !workflowId) {
        return null
      }

      const query = `[["activity.id", "=", "${objectiveActivityId}"]]`
      const result = await dispatch(
        fetchMyAggregatesAsync({ objectiveWorkflowId: workflowId, queries: { filter: query } }),
      ).unwrap()

      return result?.[0]
    },
    [dispatch, objectiveWorkflowId, objectiveActivityId, userId],
  )

  const startObjectiveWorkflow = useCallback(
    async (isRetakeClick?: boolean, objectiveWorkflowAggregateIdParam?: string) => {
      if (!aggregate?.objective || !objective) return

      const entityId = uuid()

      let startObjectiveWorkflowCondition: boolean
      let objectiveWorkflowAggregate: ObjectiveWorkflowAggregate | undefined

      const isRetakeAble = aggregate.retake
      const isObjectiveWorkflowFinished = aggregate.objectiveWorkflowAggregate?.lastObjectiveWorkflow
        ? aggregate.objectiveWorkflowAggregate.lastObjectiveWorkflow.state === ObjectiveWorkflowState.finished
        : true

      if (isRetakeClick) {
        startObjectiveWorkflowCondition = true
      } else if (isRetakeAble) {
        startObjectiveWorkflowCondition = isObjectiveWorkflowFinished
      } else {
        startObjectiveWorkflowCondition = true
      }

      if (startObjectiveWorkflowCondition) {
        objectiveWorkflowAggregate = await dispatch(
          startObjectiveWorkflowAsync({
            objectiveWorkflowAggregateId:
              objectiveWorkflowAggregateIdParam || aggregate.objectiveWorkflowAggregate?.id!,
            id: entityId,
          }),
        ).unwrap()

        logEvent(isRetakeClick ? 'course_restart' : 'objective_start', { objective })
      }

      const activityWorkflowAggregate = await getActivityWorkflowAggregates(entityId)
      const lastActivityWorkflow = activityWorkflowAggregate?.lastActivityWorkflow
      const isLastActivityWorkflowFinished = lastActivityWorkflow?.state === ActivityWorkflowState.finished

      const startActivityWorkflowCondition = () => {
        if (isRetakeClick) {
          // when we click Start again, the courses without lastObjectiveWorkflow should start the activity workflow
          if (!aggregate.objectiveWorkflowAggregate?.lastObjectiveWorkflow) {
            return true
          }

          return isObjectiveWorkflowFinished && isLastActivityWorkflowFinished
        }
        return !lastActivityWorkflow
      }

      if (startActivityWorkflowCondition()) {
        await dispatch(startActivityWorkflowAsync({ objectiveWorkflowId: entityId, activityPartIds: [] })).unwrap()
        logEvent('activity_start', { activity: objective.activity, objective })
      }

      const lastObjectiveWorkflowId =
        objectiveWorkflowAggregate?.lastObjectiveWorkflow?.id ||
        objectiveWorkflowAggregateIdParam ||
        aggregate.objectiveWorkflowAggregate?.id

      if (lastObjectiveWorkflowId) {
        await fetchActivityWorkflowAggregateFullTreeQuery({ objectiveWorkflowId: lastObjectiveWorkflowId }).unwrap()
      }

      if (objective.activity.type === ActivityType.lti) {
        window.location.href = `/${ROUTE_BASE.learner}/courses/${objectiveId}/view`
      } else {
        await refetchGetObjectiveAccessById().unwrap()
        navigateToContent(objectiveId)
      }
    },
    [
      objectiveId,
      objective,
      aggregate,
      dispatch,
      logEvent,
      getActivityWorkflowAggregates,
      fetchActivityWorkflowAggregateFullTreeQuery,
      refetchGetObjectiveAccessById,
      navigateToContent,
    ],
  )

  useEffect(() => {
    if (objectiveWorkflowId && objectiveActivityId) {
      dispatch(
        fetchMyActivityRecordsAsync({
          objectiveWorkflowId,
          queries: {
            filter: `[["activity.id","=","${objectiveActivityId}"]]`,
          },
        }),
      )
    }
  }, [objectiveWorkflowId, objectiveActivityId, dispatch])

  useEffect(() => {
    if (aggregate && objective && !aggregate.objectiveWorkflowAggregate?.lastObjectiveWorkflow && !eventDispatched) {
      logEvent('course_open_first_time', { objective })
      setEventDispatched(true)
    }
  }, [aggregate, logEvent, objective, eventDispatched])

  useEffect(() => {
    if (!objective) return

    setMyCourseBreadcrumbs([{ name: objective.name } as TreeNodeOrNodeSlug])
  }, [setMyCourseBreadcrumbs, objective])

  useEffect(() => {
    setLayers(sidebarItems)
  }, [setLayers, sidebarItems])

  if (!aggregate || !objective) {
    return null
  }

  return (
    <div className={styles.container}>
      <CourseDetailsComponent
        aggregate={aggregate}
        activityRecords={activityRecords}
        objectiveRecords={objectiveRecords}
        onStartWorkflow={startObjectiveWorkflow}
      />
    </div>
  )
}

export default CourseDetails
